import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EMPTY, forkJoin, of, finalize, catchError, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { APIListWrapper } from 'src/app/models/common/api-list-wrapper.model';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import {
    PosmDeliverySchedule,
    PosmDeliveryScheduleAutoAllocateResult,
    PosmDeliveryScheduleFilterCriteria,
    PosmDeliveryScheduleFilterOption,
    PosmDeliveryScheduleReleaseRequest,
    PosmDeliveryScheduleRemovalReleaseRequest
} from '../../models/posm-execution.models';
import { PosmProject } from '../../models/posm-project.model';
import { PosmService } from '../../posm.service';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';

type ScheduleFilters = {
    retailer: string[];
    store: string[];
    city: string[];
    item: string[];
    supplier: string;
    readiness: string;
    dueStatus: string;
    progressStage: string;
    releaseStatus: string;
    shipmentStatus: string;
    truck: string;
    driver: string;
};

type ScheduleFilterKey = keyof ScheduleFilters;
type MultiScheduleFilterKey = 'retailer' | 'store' | 'city' | 'item';
type FilterLabelValue = string | string[];
type FilterSummaryItem = { key: ScheduleFilterKey; label: string; value: string };
type PersistedScheduleViewState = {
    filters: ScheduleFilters;
    filterLabels: Partial<Record<ScheduleFilterKey, FilterLabelValue>>;
    filtersExpanded: boolean;
    currentPage: number;
    pageSize: number;
};
type ScheduleIssueGroupTone = 'danger' | 'warn' | 'active' | 'info';
type ScheduleIssueGroup = {
    key: string;
    label: string;
    hint: string;
    icon: string;
    tone: ScheduleIssueGroupTone;
    items: string[];
};
type ScheduleActionFeedbackTone = 'success' | 'warn' | 'danger' | 'info';
type ScheduleActionFeedbackItem = {
    scheduleLabel: string;
    status?: string;
    messages: string[];
};
type ScheduleActionFeedback = {
    action: 'allocation' | 'installation';
    title: string;
    summary: string;
    tone: ScheduleActionFeedbackTone;
    items: ScheduleActionFeedbackItem[];
};

const filterDisplayLabels: Record<ScheduleFilterKey, string> = {
    retailer: 'Retailer / Store Chain',
    store: 'Store / Location',
    city: 'City',
    item: 'Stand / Warehousing Item',
    supplier: 'Supplier',
    readiness: 'Readiness',
    dueStatus: 'Due Status',
    progressStage: 'Schedule Progress',
    releaseStatus: 'Release Status',
    shipmentStatus: 'Shipment Status',
    truck: 'Truck',
    driver: 'Driver'
};

const PAGE_SIZE_OPTIONS = [12, 25, 50];
const DEFAULT_PAGE_SIZE = 12;

const emptyScheduleFilters = (): ScheduleFilters => ({
    retailer: [],
    store: [],
    city: [],
    item: [],
    supplier: '',
    readiness: '',
    dueStatus: '',
    progressStage: '',
    releaseStatus: '',
    shipmentStatus: '',
    truck: '',
    driver: ''
});

const emptyFilterOptions = (): Record<ScheduleFilterKey, PosmDeliveryScheduleFilterOption[]> => ({
    retailer: [],
    store: [],
    city: [],
    item: [],
    supplier: [],
    readiness: [],
    dueStatus: [],
    progressStage: [],
    releaseStatus: [],
    shipmentStatus: [],
    truck: [],
    driver: []
});

const emptyFilterOptionsLoading = (): Record<ScheduleFilterKey, boolean> => ({
    retailer: false,
    store: false,
    city: false,
    item: false,
    supplier: false,
    readiness: false,
    dueStatus: false,
    progressStage: false,
    releaseStatus: false,
    shipmentStatus: false,
    truck: false,
    driver: false
});

@Component({
    selector: 'app-posm-project-delivery-schedule',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        DialogModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        PaginatorModule,
        MultiSelectModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        DynamicDialogModule,
        PageDataPanelComponent
    ],
    providers: [MessageService, DialogService],
    templateUrl: './posm-project-delivery-schedule.component.html',
    styleUrls: ['./posm-project-delivery-schedule.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectDeliveryScheduleComponent {
    public readonly pageSizeOptions = PAGE_SIZE_OPTIONS.map(value => ({ label: `${value} rows`, value }));

    private posmService = inject(PosmService);
    private router = inject(Router);
    private sanitizer = inject(DomSanitizer);
    private messageService = inject(MessageService);
    private permissionService = inject(PermissionService);

    canAddSchedule(): boolean {
        return this.permissionService.hasPermission(app_permissions.posm.projects.create);
    }

    public project = input.required<PosmProject>();
    public onDataChanged = output<void>();

    public schedules = signal<PosmDeliverySchedule[]>([]);
    public filterOptions = signal<Record<ScheduleFilterKey, PosmDeliveryScheduleFilterOption[]>>(emptyFilterOptions());
    public loading = signal(false);
    public releaseGenerating = signal(false);
    public bulkSaving = signal(false);
    public bulkAllocating = signal(false);
    public selectedScheduleIds = signal<Set<number>>(new Set<number>());
    public filtersExpanded = signal(false);
    public currentPage = signal(0);
    public pageSize = signal(DEFAULT_PAGE_SIZE);
    public totalScheduleRows = signal(0);
    public hasMoreSchedules = signal(false);
    public bulkInstallationDate = signal<Date | null>(null);
    public bulkRemovalDate = signal<Date | null>(null);
    public filters = signal<ScheduleFilters>(emptyScheduleFilters());
    public selectedFilterLabels = signal<Partial<Record<ScheduleFilterKey, FilterLabelValue>>>({});
    public filterOptionsLoading = signal<Record<ScheduleFilterKey, boolean>>(emptyFilterOptionsLoading());
    public showViewerDialog = signal(false);
    public viewerDialogHeader = signal('Open Record');
    public viewerDialogUrl = signal<SafeResourceUrl | null>(null);
    public actionFeedback = signal<ScheduleActionFeedback | null>(null);

    public filteredSchedules = computed(() => this.schedules());
    public retailerOptions = computed(() => this.filterOptions().retailer);
    public storeOptions = computed(() => this.filterOptions().store);
    public cityOptions = computed(() => this.filterOptions().city);
    public itemOptions = computed(() => this.filterOptions().item);
    public supplierOptions = computed(() => this.filterOptions().supplier);
    public readinessOptions = computed(() => this.filterOptions().readiness);
    public dueStatusOptions = computed(() => this.filterOptions().dueStatus);
    public progressStageOptions = computed(() => this.filterOptions().progressStage);
    public releaseStatusOptions = computed(() => this.filterOptions().releaseStatus);
    public shipmentStatusOptions = computed(() => this.filterOptions().shipmentStatus);
    public truckOptions = computed(() => this.filterOptions().truck);
    public driverOptions = computed(() => this.filterOptions().driver);
    public unresolvedCount = computed(() => this.schedules().filter(row => (row.mappingWarnings || []).length > 0).length);
    public dueNowCount = computed(() => this.schedules().filter(row => ['DUE_TODAY', 'OVERDUE'].includes(`${row.dueStatus || ''}`)).length);
    public visibleSchedules = computed(() => this.filteredSchedules().filter(row => !!row.id));
    public visibleInstallationEligibleSchedules = computed(() => this.visibleSchedules().filter(row => this.canGenerateInstallationRelease(row)));
    public visibleRemovalEligibleSchedules = computed(() => this.supportsRemovalFlow()
        ? this.visibleSchedules().filter(row => this.canGenerateRemovalRelease(row))
        : []);
    public selectedCount = computed(() => this.schedules().filter(row => row.id && this.selectedScheduleIds().has(row.id)).length);
    public selectedVisibleCount = computed(() => this.visibleSchedules().filter(row => row.id && this.selectedScheduleIds().has(row.id)).length);
    public selectedInstallationEligibleCount = computed(() => this.schedules().filter(row => row.id && this.selectedScheduleIds().has(row.id) && this.canGenerateInstallationRelease(row)).length);
    public selectedRemovalEligibleCount = computed(() => this.supportsRemovalFlow()
        ? this.schedules().filter(row => row.id && this.selectedScheduleIds().has(row.id) && this.canGenerateRemovalRelease(row)).length
        : 0);
    public activeFilterSummary = computed<FilterSummaryItem[]>(() => {
        const filters = this.filters();
        const labels = this.selectedFilterLabels();
        return (Object.keys(filters) as ScheduleFilterKey[])
            .map(key => {
                const value = filters[key];
                if (!this.hasFilterValue(value)) {
                    return null;
                }
                return {
                    key,
                    label: filterDisplayLabels[key],
                    value: this.summaryFilterLabel(key, value, labels[key])
                };
            })
            .filter((item): item is FilterSummaryItem => !!item);
    });
    public allVisibleEligibleSelected = computed(() => {
        const rows = this.visibleSchedules();
        const selected = this.selectedScheduleIds();
        return rows.length > 0 && rows.every(row => row.id && selected.has(row.id));
    });

    private initializedProjectId?: number;

    constructor() {
        effect(() => {
            const project = this.project();
            if (project?.id && project.id !== this.initializedProjectId) {
                this.initializeProjectView(project.id);
            }
        });
    }

    loadSchedules(): void {
        const project = this.project();
        const companyId = project.owningCompanyId;
        if (!project.id || !companyId) return;

        this.loading.set(true);
        this.posmService.getDeliverySchedulesPage(project.id, companyId, this.currentPage(), this.pageSize(), this.scheduleFilters())
            .pipe(finalize(() => this.loading.set(false))).subscribe({
            next: (result) => this.applySchedulePage(result),
            error: () => {
                this.schedules.set([]);
                this.totalScheduleRows.set(0);
                this.hasMoreSchedules.set(false);
                this.selectedScheduleIds.set(new Set<number>());
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Delivery Schedule',
                    detail: 'Unable to load delivery schedule rows.'
                });
            }
        });
    }

    generateOrderReleases(): void {
        const project = this.project();
        if (!project.id || !project.owningCompanyId) return;
        const selectedRows = this.schedules()
            .filter(row => row.id && this.selectedScheduleIds().has(row.id));
        if (!selectedRows.length) {
            this.actionFeedback.set({
                action: 'installation',
                title: 'Installation release generation',
                summary: 'No delivery schedules were selected.',
                tone: 'warn',
                items: [{
                    scheduleLabel: 'Selection required',
                    messages: ['Select one or more installation-ready delivery schedules, then try again.']
                }]
            });
            return;
        }
        const rows = selectedRows.filter(row => this.canGenerateInstallationRelease(row));
        const skippedItems = selectedRows
            .filter(row => !this.canGenerateInstallationRelease(row))
            .map(row => this.installationReleaseFeedbackItem(row));
        if (!rows.length) {
            const alreadyGenerated = selectedRows.every(row => this.hasInstallationRelease(row));
            this.actionFeedback.set({
                action: 'installation',
                title: alreadyGenerated ? 'Installation releases already exist' : 'Installation releases not generated',
                summary: alreadyGenerated
                    ? `All ${selectedRows.length} selected schedule${selectedRows.length === 1 ? ' already has' : 's already have'} an installation release.`
                    : `All ${selectedRows.length} selected schedule${selectedRows.length === 1 ? ' is' : 's are'} blocked. Resolve the issues listed below before generating releases.`,
                tone: alreadyGenerated ? 'info' : 'danger',
                items: skippedItems
            });
            return;
        }

        const request: PosmDeliveryScheduleReleaseRequest = {
            scheduleIds: rows.map(row => row.id!).filter(Boolean),
            assignmentMode: 'OWN_TRUCK'
        };
        this.actionFeedback.set({
            action: 'installation',
            title: 'Generating installation releases',
            summary: `${rows.length} eligible schedule${rows.length === 1 ? '' : 's'} will be processed.${skippedItems.length ? ` ${skippedItems.length} selected schedule${skippedItems.length === 1 ? '' : 's'} will be skipped.` : ''}`,
            tone: 'info',
            items: skippedItems
        });
        this.releaseGenerating.set(true);
        this.posmService.generateOrderReleasesFromSchedule(project.id, project.owningCompanyId, request)
            .pipe(finalize(() => this.releaseGenerating.set(false)))
            .subscribe({
                next: generatedRows => {
                    this.mergeUpdatedSchedules(generatedRows || []);
                    const generatedCount = (generatedRows || []).length;
                    const generatedIds = new Set((generatedRows || []).map(row => row.id));
                    const alreadyGeneratedRows = rows.filter(row => !generatedIds.has(row.id));
                    const reportedSkippedItems = [...skippedItems, ...alreadyGeneratedRows.map(row => ({
                        scheduleLabel: row.scheduleNumber || `Schedule #${row.id || '-'}`,
                        status: 'Already generated',
                        messages: ['An existing installation release was preserved.']
                    }))];
                    this.actionFeedback.set({
                        action: 'installation',
                        title: reportedSkippedItems.length ? 'Installation release generation completed with skipped rows' : 'Installation releases generated',
                        summary: `${generatedCount} schedule${generatedCount === 1 ? ' had' : 's had'} installation releases generated.${reportedSkippedItems.length ? ` ${reportedSkippedItems.length} selected schedule${reportedSkippedItems.length === 1 ? ' was' : 's were'} skipped.` : ''}`,
                        tone: reportedSkippedItems.length ? 'warn' : 'success',
                        items: reportedSkippedItems
                    });
                    this.selectedScheduleIds.set(new Set<number>());
                    this.loadSchedules();
                    this.onDataChanged.emit();
                },
                error: error => this.actionFeedback.set({
                    action: 'installation',
                    title: 'Installation release generation failed',
                    summary: this.extractErrorDetail(error, 'Unable to generate installation releases from the selected delivery schedules.'),
                    tone: 'danger',
                    items: skippedItems
                })
            });
    }

    generateRemovalReleases(): void {
        const project = this.project();
        if (!project.id || !project.owningCompanyId) return;
        const rows = this.schedules()
            .filter(row => row.id && this.selectedScheduleIds().has(row.id))
            .filter(row => this.canGenerateRemovalRelease(row));
        if (!rows.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Removal Releases',
                detail: 'Select at least one removal-ready delivery schedule row.'
            });
            return;
        }

        const request: PosmDeliveryScheduleRemovalReleaseRequest = {
            scheduleIds: rows.map(row => row.id!).filter(Boolean),
            assignmentMode: 'OWN_TRUCK'
        };
        this.releaseGenerating.set(true);
        this.posmService.generateRemovalOrderReleasesFromSchedule(project.id, project.owningCompanyId, request)
            .pipe(finalize(() => this.releaseGenerating.set(false)))
            .subscribe({
                next: schedules => {
                    this.applyScheduleRows(schedules || []);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Removal Releases',
                        detail: 'Removal releases were generated from the selected delivery schedule rows.'
                    });
                    this.onDataChanged.emit();
                },
                error: () => this.messageService.add({
                    severity: 'error',
                    summary: 'Removal Releases',
                    detail: 'Unable to generate removal releases from the delivery schedule.'
                })
            });
    }

    openNewSchedule(): void {
        const project = this.project();
        if (project.id) {
            this.router.navigate(['/posm/executions', project.id, 'delivery-schedule', 'new']);
        }
    }

    openScheduleDetail(row: PosmDeliverySchedule): void {
        const project = this.project();
        if (project.id && row.id) {
            this.router.navigate(['/posm/executions', project.id, 'delivery-schedule', row.id]);
        }
    }

    toggleVisibleSelection(checked: boolean): void {
        const next = new Set(this.selectedScheduleIds());
        for (const row of this.visibleSchedules()) {
            if (!row.id) continue;
            if (checked) {
                next.add(row.id);
            } else {
                next.delete(row.id);
            }
        }
        this.selectedScheduleIds.set(next);
    }

    toggleRowSelection(row: PosmDeliverySchedule, checked: boolean): void {
        if (!row.id) return;
        const next = new Set(this.selectedScheduleIds());
        if (checked) {
            next.add(row.id);
        } else {
            next.delete(row.id);
        }
        this.selectedScheduleIds.set(next);
    }

    applyBulkInstallationDate(): void {
        const project = this.project();
        const companyId = project.owningCompanyId;
        const installationDate = this.bulkInstallationDate();
        const rows = this.schedules().filter(row => row.id && this.selectedScheduleIds().has(row.id));
        if (!project.id || !companyId || !installationDate) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Bulk Installation Date',
                detail: 'Select an installation date first.'
            });
            return;
        }
        if (!rows.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Bulk Installation Date',
                detail: 'Select at least one delivery schedule row.'
            });
            return;
        }
        this.bulkSaving.set(true);
        forkJoin(rows.map(row => this.posmService.updateDeliverySchedule(project.id!, row.id!, companyId, {
            id: row.id,
            storeId: row.storeId,
            projectItemId: row.projectItemId,
            supplierClientId: row.supplierClientId,
            plannedInstallationDateOverride: installationDate,
            contactNumberOverride: row.contactNumberOverride,
            comment: row.comment
        }).pipe(catchError(() => of(null))))).pipe(finalize(() => this.bulkSaving.set(false))).subscribe({
            next: updatedRows => {
                const savedRows = (updatedRows || []).filter((row): row is PosmDeliverySchedule => !!row);
                const skippedCount = rows.length - savedRows.length;
                this.mergeUpdatedSchedules(savedRows);
                this.messageService.add({
                    severity: savedRows.length ? 'success' : 'warn',
                    summary: 'Bulk Installation Date',
                    detail: savedRows.length
                        ? `Installation date applied to ${savedRows.length} rows.${skippedCount ? ` ${skippedCount} rows were skipped.` : ''}`
                        : 'No selected rows could be updated.'
                });
                this.onDataChanged.emit();
            },
            error: () => this.messageService.add({
                severity: 'error',
                summary: 'Bulk Installation Date',
                detail: 'Unable to apply the installation date to the selected rows.'
            })
        });
    }

    applyBulkRemovalDate(): void {
        const project = this.project();
        const companyId = project.owningCompanyId;
        const removalDate = this.bulkRemovalDate();
        const rows = this.schedules().filter(row => row.id && this.selectedScheduleIds().has(row.id));
        if (!project.id || !companyId || !removalDate) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Bulk Removal Date',
                detail: 'Select a removal date first.'
            });
            return;
        }
        if (!rows.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Bulk Removal Date',
                detail: 'Select at least one delivery schedule row.'
            });
            return;
        }
        this.bulkSaving.set(true);
        forkJoin(rows.map(row => this.posmService.updateDeliverySchedule(project.id!, row.id!, companyId, {
            id: row.id,
            storeId: row.storeId,
            projectItemId: row.projectItemId,
            supplierClientId: row.supplierClientId,
            plannedInstallationDateOverride: row.plannedInstallationDateOverride,
            plannedRemovalDateOverride: removalDate,
            returnDestinationType: row.returnDestinationType,
            returnWarehouseId: row.returnWarehouseId,
            returnLocationId: row.returnLocationId,
            returnDisposition: row.returnDisposition,
            returnConditionId: row.returnConditionId,
            contactNumberOverride: row.contactNumberOverride,
            comment: row.comment
        }).pipe(catchError(() => of(null))))).pipe(finalize(() => this.bulkSaving.set(false))).subscribe({
            next: updatedRows => {
                const savedRows = (updatedRows || []).filter((row): row is PosmDeliverySchedule => !!row);
                const skippedCount = rows.length - savedRows.length;
                this.mergeUpdatedSchedules(savedRows);
                this.messageService.add({
                    severity: savedRows.length ? 'success' : 'warn',
                    summary: 'Bulk Removal Date',
                    detail: savedRows.length
                        ? `Removal date applied to ${savedRows.length} rows.${skippedCount ? ` ${skippedCount} rows were skipped.` : ''}`
                        : 'No selected rows could be updated.'
                });
                this.onDataChanged.emit();
            },
            error: () => this.messageService.add({
                severity: 'error',
                summary: 'Bulk Removal Date',
                detail: 'Unable to apply the removal date to the selected rows.'
            })
        });
    }

    applyBulkAllocation(): void {
        const project = this.project();
        const companyId = project.owningCompanyId;
        const selectedRows = this.schedules().filter(row => row.id && this.selectedScheduleIds().has(row.id));
        if (!project.id || !companyId) {
            return;
        }
        if (!selectedRows.length) {
            this.actionFeedback.set({
                action: 'allocation',
                title: 'Auto allocation',
                summary: 'No delivery schedules were selected.',
                tone: 'warn',
                items: [{
                    scheduleLabel: 'Selection required',
                    messages: ['Select one or more delivery schedules, then run auto allocation again.']
                }]
            });
            return;
        }
        const request = {
            scheduleIds: selectedRows.map(row => row.id!).filter(Boolean)
        };
        this.bulkAllocating.set(true);
        this.posmService.previewAutoAllocateDeliverySchedules(project.id, companyId, request)
            .pipe(
                switchMap(preview => {
                    this.handleAutoAllocatePreview(preview);
                    if (!Number(preview?.allocatableCount || 0)) {
                        return EMPTY;
                    }
                    return this.posmService.autoAllocateDeliverySchedules(project.id!, companyId, request);
                }),
                finalize(() => this.bulkAllocating.set(false))
            )
            .subscribe({
                next: result => {
                    this.handleAutoAllocateResult(result);
                    this.loadSchedules();
                    this.onDataChanged.emit();
                },
                error: error => this.actionFeedback.set({
                    action: 'allocation',
                    title: 'Auto allocation failed',
                    summary: this.extractErrorDetail(error, 'Unable to complete auto allocation for the selected schedules.'),
                    tone: 'danger',
                    items: []
                })
            });
    }

    clearActionFeedback(): void {
        this.actionFeedback.set(null);
    }

    actionFeedbackIcon(tone: ScheduleActionFeedbackTone): string {
        switch (tone) {
            case 'success':
                return 'pi pi-check-circle';
            case 'danger':
                return 'pi pi-times-circle';
            case 'warn':
                return 'pi pi-exclamation-triangle';
            default:
                return 'pi pi-info-circle';
        }
    }

    clearSelection(): void {
        this.selectedScheduleIds.set(new Set<number>());
    }

    clearFilters(): void {
        this.currentPage.set(0);
        this.filters.set(emptyScheduleFilters());
        this.selectedFilterLabels.set({});
        this.resetFilterOptions();
        this.persistViewState();
        this.loadSchedules();
    }

    updateFilter(key: keyof ScheduleFilters, value: any): void {
        const normalizedValue = this.normalizeFilterValue(key, value);
        this.currentPage.set(0);
        this.filters.update(current => ({
            ...current,
            [key]: normalizedValue
        }));
        this.selectedFilterLabels.update(current => ({
            ...current,
            [key]: this.resolveFilterLabel(key, normalizedValue)
        }));
        this.persistViewState();
        this.hydrateSelectedFilterOptions();
        this.loadSchedules();
    }

    onPageChange(event: PaginatorState): void {
        const nextPage = event.page ?? 0;
        const nextPageSize = event.rows ?? this.pageSize();
        if (nextPage === this.currentPage() && nextPageSize === this.pageSize()) {
            return;
        }
        if (nextPageSize !== this.pageSize()) {
            this.pageSize.set(this.normalizePageSize(nextPageSize));
        }
        this.currentPage.set(nextPage);
        this.persistViewState();
        this.loadSchedules();
    }

    onPageSizeChange(size: number | null | undefined): void {
        const nextSize = this.normalizePageSize(size);
        if (nextSize === this.pageSize()) {
            return;
        }
        this.pageSize.set(nextSize);
        this.currentPage.set(0);
        this.persistViewState();
        this.loadSchedules();
    }

    toggleFilters(): void {
        this.filtersExpanded.update(current => !current);
        this.persistViewState();
    }

    locationLabel(row: PosmDeliverySchedule): string {
        return row.location?.name || row.storeName || row.storeNumber || 'POSM Store';
    }

    retailerLabel(row: PosmDeliverySchedule): string {
        return row.location?.retailer?.name || row.customerName || 'Retailer pending';
    }

    retailerSourceLabel(row: PosmDeliverySchedule): string {
        return row.location?.retailer?.name ? 'Approved retailer' : 'Imported customer name';
    }

    locationReference(row: PosmDeliverySchedule): string {
        return row.location?.externalLocationReference || row.storeNumber || '-';
    }

    normalizedJobTypes(): string[] {
        const project = this.project();
        const values = [
            ...(project.jobTypes || []),
            ...(project.jobTypeCodes || []),
            project.jobType
        ].filter((value): value is string => !!value);
        const normalized = values
            .map(value => `${value}`.trim().toLowerCase())
            .filter(Boolean);
        return [...new Set(normalized)];
    }

    supportsRemovalFlow(): boolean {
        return this.normalizedJobTypes().some(type => type === 'removal' || type === 'collection');
    }

    itemLabel(row: PosmDeliverySchedule): string {
        return row.inventoryItem?.itemName || row.standName || 'Unmapped stand';
    }

    itemReference(row: PosmDeliverySchedule): string {
        return row.inventoryItem?.skuNumber || row.inventoryItem?.itemNumber || row.standSize || '-';
    }

    itemNumber(row: PosmDeliverySchedule): string {
        return row.inventoryItem?.itemNumber || '-';
    }

    itemSku(row: PosmDeliverySchedule): string {
        return row.inventoryItem?.skuNumber || '-';
    }

    itemSize(row: PosmDeliverySchedule): string {
        return row.standSize || '-';
    }

    coverageMode(row: PosmDeliverySchedule): 'PHYSICAL' | 'LOGICAL' {
        return `${row.coverageMode || ''}`.toUpperCase() === 'LOGICAL' ? 'LOGICAL' : 'PHYSICAL';
    }

    coverageModeLabel(row: PosmDeliverySchedule): string {
        return this.coverageMode(row) === 'LOGICAL' ? 'Supplier-confirmed supply' : 'Stock-backed supply';
    }

    coverageModeSeverity(row: PosmDeliverySchedule): string {
        return this.coverageMode(row) === 'LOGICAL' ? 'info' : 'secondary';
    }

    sourceCoverageLabel(row: PosmDeliverySchedule): string {
        if (this.coverageMode(row) === 'PHYSICAL') {
            return row.sourceWarehouseName || 'Warehouse source pending';
        }
        return row.sourceWarehouseName || row.supplierClientName || row.supplier || 'Supplier pickup';
    }

    supplySourceLabel(row: PosmDeliverySchedule): string {
        return this.sourceCoverageLabel(row);
    }

    supplySourceDetail(row: PosmDeliverySchedule): string {
        if (row.supplierInboundRequired) {
            return row.inboundHandoffWarehouseName
                ? `Supplier inbound to ${row.inboundHandoffWarehouseName}`
                : 'Supplier inbound setup required';
        }
        return `${this.coverageModeLabel(row)} | ${this.sourceCoverageLabel(row)}`;
    }

    installationStatusLabel(row: PosmDeliverySchedule): string {
        const normalized = `${row.readinessStatus || ''}`.toUpperCase();
        if (normalized === 'READY') return 'Install ready';
        if (normalized.includes('MAPPING')) return 'Install setup needed';
        return 'Install planning pending';
    }

    removalStatusLabel(row: PosmDeliverySchedule): string {
        const normalized = `${row.removalReadinessStatus || ''}`.toUpperCase();
        if (normalized === 'READY') return 'Removal ready';
        if (normalized.includes('MAPPING')) return 'Removal setup needed';
        return 'Removal planning pending';
    }

    allocationLabel(row: PosmDeliverySchedule): string {
        switch (`${row.allocationStatus || ''}`.toUpperCase()) {
            case 'ALLOCATED':
                return 'Materials allocated';
            case 'PARTIAL':
                return 'Materials partly allocated';
            default:
                return 'Material allocation pending';
        }
    }

    progressLabel(row: PosmDeliverySchedule): string {
        const normalized = `${row.progressStage || row.status || ''}`.trim().toUpperCase();
        switch (normalized) {
            case 'DELIVERED':
                return 'Delivered';
            case 'IN_TRANSIT':
                return 'In transit';
            case 'TRUCK_PLANNED':
                return 'Truck planned';
            case 'SCHEDULED':
                return 'Scheduled';
            case 'TASK_CREATED':
                return 'Task created';
            case 'MAPPING_REQUIRED':
                return 'Setup needed';
            case 'RELEASE_ERROR':
                return 'Release issue';
            case 'EXCEPTION':
                return 'Execution issue';
            case 'NEW':
            case 'PENDING':
            case '':
                return 'Not started';
            default:
                return this.toTitleCase(normalized.replace(/_/g, ' '));
        }
    }

    returnOutcomeLabel(row: PosmDeliverySchedule): string {
        const normalized = `${row.returnOutcomeStatus || ''}`.trim().toUpperCase();
        if (!normalized) return 'Return not started';
        switch (normalized) {
            case 'PENDING_RETURN':
                return 'Return pending';
            case 'RETURN_DELIVERED':
                return 'Return delivered';
            case 'RECEIPT_COMPLETED':
                return 'Return received';
            case 'DESTRUCTION_DRAFTED':
                return 'Destruction drafted';
            case 'REFURBISHMENT_REQUESTED':
                return 'Refurbishment requested';
            case 'COMPLETED':
                return 'Return completed';
            default:
                return this.toTitleCase(normalized.replace(/_/g, ' '));
        }
    }

    dueLabel(row: PosmDeliverySchedule): string {
        const normalized = `${row.dueStatus || ''}`.trim().toUpperCase();
        switch (normalized) {
            case 'OVERDUE':
                return 'Overdue';
            case 'DUE_TODAY':
                return 'Due today';
            case 'UPCOMING':
                return 'Upcoming';
            case 'UNSCHEDULED':
            case '':
                return 'Not scheduled';
            default:
                return this.toTitleCase(normalized.replace(/_/g, ' '));
        }
    }

    scheduleDatesLabel(row: PosmDeliverySchedule): string {
        const parts = [`Install ${this.formattedDate(this.installationCommitmentDate(row))}`];
        if (this.supportsRemovalFlow()) {
            parts.push(`Remove ${this.formattedDate(this.removalCommitmentDate(row))}`);
        }
        return parts.join(' | ');
    }

    scheduleWarnings(row: PosmDeliverySchedule): string[] {
        const warnings = [
            ...(row.mappingWarnings || []),
            ...(row.allocationWarnings || [])
        ];
        if (this.supportsRemovalFlow()) {
            warnings.push(...(row.removalWarnings || []));
            warnings.push(...(row.returnOutcomeWarnings || []));
        }
        return warnings
            .map(warning => this.cleanIssueText(warning))
            .filter(Boolean)
            .filter((warning, index, items) => items.findIndex(candidate => this.sameIssue(candidate, warning)) === index);
    }

    scheduleIssueGroups(row: PosmDeliverySchedule): ScheduleIssueGroup[] {
        const groups = new Map<string, ScheduleIssueGroup>();
        for (const warning of this.scheduleWarnings(row)) {
            const descriptor = this.scheduleIssueDescriptor(warning);
            const existing = groups.get(descriptor.key);
            if (existing) {
                existing.items.push(warning);
                continue;
            }
            groups.set(descriptor.key, {
                ...descriptor,
                items: [warning]
            });
        }

        const statusReason = this.scheduleStatusReason(row);
        if (statusReason) {
            const currentTone = this.scheduleTone(row);
            const tone: ScheduleIssueGroupTone = currentTone === 'success' ? 'info' : currentTone;
            groups.set('status', {
                key: 'status',
                label: 'Current status',
                hint: 'Latest execution note',
                icon: 'pi pi-info-circle',
                tone,
                items: [statusReason]
            });
        }

        return Array.from(groups.values());
    }

    isShipmentExecutionActive(row: PosmDeliverySchedule): boolean {
        const activeStatuses = new Set([
            'IN_TRANSIT',
            'OUT_FOR_DELIVERY',
            'DISPATCHED',
            'EN_ROUTE',
            'DELIVERY_IN_PROGRESS',
            'SHIPMENT_IN_PROGRESS'
        ]);
        return [
            row.progressStage,
            row.shipmentStatus,
            row.transportStatus,
            row.status
        ].some(value => activeStatuses.has(`${value || ''}`.trim().toUpperCase()));
    }

    scheduleHasBlockingIssues(row: PosmDeliverySchedule): boolean {
        const executionStatuses = [
            row.progressStage,
            row.shipmentStatus,
            row.transportStatus,
            row.releaseStatus,
            row.status
        ].map(value => `${value || ''}`.trim().toUpperCase());
        return this.scheduleWarnings(row).length > 0
            || executionStatuses.some(status => status.includes('ERROR') || status === 'EXCEPTION');
    }

    scheduleIssueSummaryLabel(row: PosmDeliverySchedule): string {
        if (this.scheduleHasBlockingIssues(row)) return 'Attention required';
        if (this.isShipmentExecutionActive(row)) return 'Execution underway';
        return 'Current progress';
    }

    scheduleIssueSummaryText(row: PosmDeliverySchedule): string {
        if (this.scheduleHasBlockingIssues(row)) {
            const count = this.scheduleWarnings(row).length || 1;
            return `${count} issue${count === 1 ? '' : 's'} requiring attention`;
        }
        if (this.isShipmentExecutionActive(row)) {
            return 'Shipment and delivery execution is progressing toward completion';
        }
        return this.scheduleStatusReason(row) || 'Review the current execution status';
    }

    hasScheduleIssueContent(row: PosmDeliverySchedule): boolean {
        return this.scheduleIssueGroups(row).length > 0 || !!this.cleanIssueText(row.nextAction);
    }

    scheduleIssueCount(row: PosmDeliverySchedule): number {
        return this.scheduleIssueGroups(row).reduce((total, group) => total + group.items.length, 0);
    }

    supplierLabel(row: PosmDeliverySchedule): string {
        return row.supplierClientName || row.supplier || 'Supplier not mapped';
    }

    readinessSeverity(value: string | null | undefined): string {
        const normalized = `${value || ''}`.toUpperCase();
        if (normalized === 'READY') return 'success';
        if (normalized.includes('MAPPING')) return 'warning';
        return 'info';
    }

    statusSeverity(status: string | null | undefined): string {
        const normalized = `${status || ''}`.trim().toUpperCase();
        if (!normalized) return 'info';
        if (normalized.includes('COMPLETE') || normalized.includes('DONE') || normalized.includes('DELIVERED') || normalized.includes('APPROVED')) return 'success';
        if (normalized.includes('PENDING') || normalized.includes('NEW') || normalized.includes('DRAFT')) return 'warning';
        if (normalized.includes('CANCEL') || normalized.includes('HOLD') || normalized.includes('ERROR') || normalized.includes('REJECT')) return 'danger';
        return 'info';
    }

    progressSeverity(stage: string | null | undefined): string {
        const normalized = `${stage || ''}`.trim().toUpperCase();
        if (normalized === 'DELIVERED') return 'success';
        if (normalized === 'MAPPING_REQUIRED' || normalized === 'RELEASE_ERROR' || normalized === 'EXCEPTION') return 'danger';
        if (normalized === 'SCHEDULED' || normalized === 'TASK_CREATED') return 'warning';
        return 'info';
    }

    scheduleTone(row: PosmDeliverySchedule): 'danger' | 'warn' | 'active' | 'success' | 'info' {
        const dueStatus = `${row.dueStatus || ''}`.trim().toUpperCase();
        const stage = `${row.progressStage || row.status || ''}`.trim().toUpperCase();
        if (['RELEASE_ERROR', 'EXCEPTION'].includes(stage)) {
            return 'danger';
        }
        if (stage === 'DELIVERED' || `${row.returnOutcomeStatus || ''}`.trim().toUpperCase() === 'COMPLETED') {
            return 'success';
        }
        if (this.isShipmentExecutionActive(row)) {
            return 'active';
        }
        if (dueStatus === 'OVERDUE' || this.scheduleWarnings(row).length || stage === 'MAPPING_REQUIRED') {
            return 'danger';
        }
        if (dueStatus === 'DUE_TODAY' || ['SCHEDULED', 'TASK_CREATED', 'TRUCK_PLANNED'].includes(stage)) {
            return 'warn';
        }
        return 'info';
    }

    progressWidth(row: PosmDeliverySchedule): string {
        const value = Math.max(0, Math.min(100, Number(row.progressPercent || 0)));
        return `${value}%`;
    }

    canGenerateInstallationRelease(row: PosmDeliverySchedule): boolean {
        if (!row.id) return false;
        if (this.hasInstallationRelease(row)) return false;
        if (`${row.readinessStatus || ''}`.toUpperCase() !== 'READY') return false;
        if (`${row.allocationStatus || ''}`.toUpperCase() !== 'ALLOCATED') return false;
        const stage = `${row.progressStage || row.status || ''}`.toUpperCase();
        return !['DELIVERED', 'IN_TRANSIT', 'TRUCK_PLANNED'].includes(stage);
    }

    private hasInstallationRelease(row: PosmDeliverySchedule): boolean {
        return !!row.transportReleaseId || (row.transportReleaseGroups || []).some(group => !!group.transportReleaseId);
    }

    canGenerateRemovalRelease(row: PosmDeliverySchedule): boolean {
        if (!this.supportsRemovalFlow()) return false;
        if (!row.id) return false;
        const readiness = `${row.removalReadinessStatus || ''}`.toUpperCase();
        if (readiness !== 'READY') return false;
        const status = `${row.removalReleaseStatus || row.removalTransportStatus || row.removalShipmentStatus || ''}`.toUpperCase();
        return !['DELIVERED', 'IN_TRANSIT', 'TRUCK_PLANNED', 'COMPLETED'].includes(status);
    }

    isRowSelected(row: PosmDeliverySchedule): boolean {
        return !!(row.id && this.selectedScheduleIds().has(row.id));
    }

    openLocation(row: PosmDeliverySchedule): void {
        if (row.location?.id) {
            this.openInDialog('Location', `/location/view/${row.location.id}`);
        }
    }

    openItem(row: PosmDeliverySchedule): void {
        if (row.inventoryItem?.id) {
            this.openInDialog('Warehousing Item', `/warehousing/items/view/${row.inventoryItem.id}`);
        }
    }

    openSupplier(row: PosmDeliverySchedule): void {
        if (row.supplierClientId) {
            this.openInDialog('Supplier', `/crm/clients/${row.supplierClientId}`);
        }
    }

    closeViewerDialog(): void {
        this.showViewerDialog.set(false);
        this.viewerDialogUrl.set(null);
    }

    createSupplier(row?: PosmDeliverySchedule): void {
        this.router.navigate(['/crm/clients/new'], {
            queryParams: {
                category: 'SUPPLIER',
                companyName: row?.supplier || undefined,
                returnUrl: `/posm/executions/${this.project().id}`
            }
        });
    }

    formattedDate(value: unknown): string {
        if (!value) return '-';
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
    }

    installationCommitmentDate(row: PosmDeliverySchedule): Date | undefined {
        return row.plannedInstallationDateOverride || row.plannedInstallationDate || row.installationDate;
    }

    removalCommitmentDate(row: PosmDeliverySchedule): Date | undefined {
        return row.plannedRemovalDateOverride || row.plannedRemovalDate || row.removalDate;
    }

    private openInDialog(title: string, route: string): void {
        const baseUrl = window.location.origin.replace(/\/$/, '');
        this.viewerDialogHeader.set(title);
        this.viewerDialogUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(`${baseUrl}${route}`));
        this.showViewerDialog.set(true);
    }

    projectClientLabel(): string {
        const project = this.project();
        return project.crmClient?.companyName || project.client?.name || '-';
    }

    loadFilterOptions(key: ScheduleFilterKey, query = ''): void {
        const project = this.project();
        const companyId = project.owningCompanyId;
        if (!project.id || !companyId) {
            return;
        }
        this.filterOptionsLoading.update(current => ({
            ...current,
            [key]: true
        }));
        this.posmService.getDeliveryScheduleFilterValues(project.id, companyId, key, query, 0, 25, this.scheduleFilters())
            .pipe(finalize(() => {
                this.filterOptionsLoading.update(current => ({
                    ...current,
                    [key]: false
                }));
            }))
            .subscribe({
                next: result => {
                    const options = this.mergeSelectedOption(key, result?.items || []);
                    this.filterOptions.update(current => ({
                        ...current,
                        [key]: options
                    }));
                },
                error: () => {
                    this.filterOptions.update(current => ({
                        ...current,
                        [key]: []
                    }));
                }
            });
    }

    private normalizeRow(row: PosmDeliverySchedule): PosmDeliverySchedule {
        return {
            ...row,
            jobDate: this.toDate(row.jobDate),
            installationDate: this.toDate(row.installationDate),
            plannedInstallationDate: this.toDate(row.plannedInstallationDate),
            plannedInstallationDateOverride: this.toDate(row.plannedInstallationDateOverride),
            removalDate: this.toDate(row.removalDate),
            plannedRemovalDate: this.toDate(row.plannedRemovalDate),
            plannedRemovalDateOverride: this.toDate(row.plannedRemovalDateOverride),
            releaseGeneratedAt: this.toDate(row.releaseGeneratedAt),
            releasedToTruckPlanAt: this.toDate(row.releasedToTruckPlanAt),
            actualDeliveryDate: this.toDate(row.actualDeliveryDate),
            statusDate: this.toDate(row.statusDate),
            returnOutcomeCompletedAt: this.toDate(row.returnOutcomeCompletedAt)
        };
    }

    private applyScheduleRows(rows: PosmDeliverySchedule[]): void {
        const normalized = (rows || []).map(row => this.normalizeRow(row));
        this.schedules.set(normalized);
        const validIds = new Set(normalized.map(row => row.id).filter((id): id is number => !!id));
        this.selectedScheduleIds.update(current => new Set([...current].filter(id => validIds.has(id))));
    }

    private applySchedulePage(result: APIListWrapper<PosmDeliverySchedule> | null | undefined): void {
        const totalRows = Number(result?.count || 0);
        const pageRows = result?.items || [];
        const returnedLimit = Number(result?.limit || 0);
        const responsePageSize = this.normalizePageSize(
            returnedLimit > 0
                ? returnedLimit
                : result?.hasMore && pageRows.length
                    ? pageRows.length
                    : this.pageSize()
        );
        if (responsePageSize !== this.pageSize()) {
            this.pageSize.set(responsePageSize);
            this.persistViewState();
        }
        const maxPage = totalRows > 0 ? Math.max(0, Math.ceil(totalRows / this.pageSize()) - 1) : 0;
        if (!pageRows.length && totalRows > 0 && this.currentPage() > maxPage) {
            this.currentPage.set(maxPage);
            this.persistViewState();
            this.loadSchedules();
            return;
        }
        this.totalScheduleRows.set(totalRows);
        this.hasMoreSchedules.set(!!result?.hasMore);
        this.applyScheduleRows(pageRows);
    }

    private mergeUpdatedSchedules(rows: PosmDeliverySchedule[]): void {
        if (!rows.length) {
            return;
        }
        const byId = new Map(rows.filter(row => row.id).map(row => [row.id!, this.normalizeRow(row)]));
        this.schedules.update(current => current.map(row => row.id && byId.has(row.id) ? byId.get(row.id)! : row));
    }

    private toDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    private toTitleCase(value: string): string {
        return value
            .toLowerCase()
            .split(' ')
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    private scheduleFilters(): PosmDeliveryScheduleFilterCriteria {
        return { ...this.filters() };
    }

    private resetFilterOptions(): void {
        this.filterOptions.set(emptyFilterOptions());
        this.hydrateSelectedFilterOptions();
    }

    clearSingleFilter(key: ScheduleFilterKey): void {
        this.updateFilter(key, this.isMultiFilterKey(key) ? [] : '');
    }

    private handleAutoAllocatePreview(result: PosmDeliveryScheduleAutoAllocateResult | null | undefined): void {
        const selected = Number(result?.selectedCount || 0);
        const allocatable = Number(result?.allocatableCount || 0);
        const fullyAllocatable = Number(result?.fullyAllocatableCount || 0);
        const partiallyAllocatable = Number(result?.partiallyAllocatableCount || 0);
        const skipped = Number(result?.skippedCount || 0);
        const summary = allocatable
            ? `${allocatable} of ${selected} selected rows can receive material allocation`
            : `None of the ${selected} selected rows can receive material allocation`;
        const counts = [
            fullyAllocatable ? `${fullyAllocatable} full` : '',
            partiallyAllocatable ? `${partiallyAllocatable} partial` : '',
            skipped ? `${skipped} skipped` : ''
        ].filter(Boolean).join(', ');
        const warehousePlan = this.warehouseAllocationSummary(result);
        const items = this.autoAllocationFeedbackItems(result, 'FULLY_ALLOCATABLE');
        this.actionFeedback.set({
            action: 'allocation',
            title: allocatable ? 'Auto allocation preview' : 'Auto allocation cannot proceed',
            summary: `${summary}${counts ? ` (${counts}).` : '.'}${warehousePlan ? ` ${warehousePlan}` : ''}${allocatable ? ' The allocation plan is being applied.' : ' Review the schedule-level reasons below.'}`,
            tone: !allocatable ? 'danger' : (skipped || partiallyAllocatable ? 'warn' : 'info'),
            items
        });
    }

    private handleAutoAllocateResult(result: PosmDeliveryScheduleAutoAllocateResult | null | undefined): void {
        const selected = Number(result?.selectedCount || 0);
        const processed = Number(result?.processedCount || 0);
        const fullyAllocated = Number(result?.fullyAllocatedCount || 0);
        const partiallyAllocated = Number(result?.partiallyAllocatedCount || 0);
        const skipped = Number(result?.skippedCount || 0);
        const failed = Number(result?.failedCount || 0);
        const fragments = [];
        if (fullyAllocated) {
            fragments.push(`${fullyAllocated} fully allocated`);
        }
        if (partiallyAllocated) {
            fragments.push(`${partiallyAllocated} partially allocated`);
        }
        if (skipped) {
            fragments.push(`${skipped} skipped`);
        }
        if (failed) {
            fragments.push(`${failed} failed`);
        }
        const items = this.autoAllocationFeedbackItems(result, 'FULLY_ALLOCATED');
        const warehousePlan = this.warehouseAllocationSummary(result);
        const hasProblems = skipped > 0 || failed > 0 || partiallyAllocated > 0 || items.length > 0;
        this.actionFeedback.set({
            action: 'allocation',
            title: hasProblems ? 'Auto allocation completed with issues' : 'Auto allocation completed',
            summary: selected
                ? `${selected} selected; ${processed} allocated${fragments.length ? `: ${fragments.join(', ')}.` : '.'}${warehousePlan ? ` ${warehousePlan}` : ''}`
                : 'No selected rows were processed.',
            tone: failed || (!fullyAllocated && !partiallyAllocated) ? 'danger' : (hasProblems ? 'warn' : 'success'),
            items
        });
    }

    private autoAllocationFeedbackItems(
        result: PosmDeliveryScheduleAutoAllocateResult | null | undefined,
        successfulStatus: string
    ): ScheduleActionFeedbackItem[] {
        const items = (result?.rowResults || [])
            .filter(row => `${row.status || ''}`.trim().toUpperCase() !== successfulStatus)
            .map(row => ({
                scheduleLabel: row.scheduleNumber || `Schedule #${row.scheduleId || '-'}`,
                status: this.toTitleCase(`${row.status || row.reasonCode || 'Attention required'}`.replace(/_/g, ' ')),
                messages: [row.message || row.reasonCode || 'This schedule could not be allocated.']
            }));
        const existingMessages = new Set(items.flatMap(item => item.messages).map(message => this.issueKey(message)));
        for (const warning of result?.warnings || []) {
            const message = this.cleanIssueText(warning);
            if (!message || existingMessages.has(this.issueKey(message))) {
                continue;
            }
            items.push({
                scheduleLabel: 'Allocation warning',
                status: 'Attention required',
                messages: [message]
            });
            existingMessages.add(this.issueKey(message));
        }
        return items;
    }

    private warehouseAllocationSummary(result: PosmDeliveryScheduleAutoAllocateResult | null | undefined): string {
        const summaries = (result?.rowResults || [])
            .filter(row => (row.warehouseAllocations || []).length > 0)
            .map(row => {
                const split = (row.warehouseAllocations || [])
                    .map(allocation => `${allocation.quantity || 0} from ${allocation.warehouseName || `warehouse #${allocation.warehouseId || '-'}`}`)
                    .join(', ');
                return `${row.scheduleNumber || `Schedule #${row.scheduleId || '-'}`}: ${split}.`;
            });
        return summaries.length ? `Warehouse split — ${summaries.join(' ')}` : '';
    }

    private installationReleaseFeedbackItem(row: PosmDeliverySchedule): ScheduleActionFeedbackItem {
        return {
            scheduleLabel: row.scheduleNumber || `Schedule #${row.id || '-'}`,
            status: 'Not submitted',
            messages: this.installationReleaseBlockers(row)
        };
    }

    private installationReleaseBlockers(row: PosmDeliverySchedule): string[] {
        const blockers: string[] = [];
        if (this.hasInstallationRelease(row)) {
            blockers.push('An installation release already exists for this schedule.');
        }
        const readiness = `${row.readinessStatus || ''}`.trim().toUpperCase();
        const allocation = `${row.allocationStatus || ''}`.trim().toUpperCase();
        const stage = `${row.progressStage || row.status || ''}`.trim().toUpperCase();

        if (readiness !== 'READY') {
            blockers.push(...(row.mappingWarnings || []));
            if (!(row.mappingWarnings || []).length) {
                blockers.push('Installation setup is not ready.');
            }
        }
        if (allocation !== 'ALLOCATED') {
            blockers.push(...(row.allocationWarnings || []));
            if (!(row.allocationWarnings || []).length) {
                blockers.push('Required materials are not fully allocated.');
            }
        }
        if (['DELIVERED', 'IN_TRANSIT', 'TRUCK_PLANNED'].includes(stage)) {
            blockers.push(`Installation execution is already ${this.toTitleCase(stage.replace(/_/g, ' '))}; another release cannot be generated.`);
        }

        return blockers
            .map(message => this.cleanIssueText(message))
            .filter(Boolean)
            .filter((message, index, messages) =>
                messages.findIndex(candidate => this.sameIssue(candidate, message)) === index);
    }

    private extractErrorDetail(error: unknown, fallback: string): string {
        const httpError = error as HttpErrorResponse | null | undefined;
        const payload = httpError?.error as { message?: unknown; error?: unknown; detail?: unknown } | string | null | undefined;
        if (typeof payload === 'string' && payload.trim()) {
            return payload.trim();
        }
        const structuredPayload = typeof payload === 'object' && payload !== null ? payload : null;
        const structuredMessage = [structuredPayload?.message, structuredPayload?.detail, structuredPayload?.error]
            .map(value => `${value || ''}`.trim())
            .find(Boolean);
        if (structuredMessage) {
            return structuredMessage;
        }
        const message = `${httpError?.message || ''}`.trim();
        return message || fallback;
    }

    private storageKey(): string | null {
        const projectId = this.project().id;
        return projectId ? `posm-delivery-schedule-view:${projectId}` : null;
    }

    private initializeProjectView(projectId: number): void {
        this.initializedProjectId = projectId;
        this.restoreViewState();
        this.resetFilterOptions();
        this.loadSchedules();
    }

    private applyDefaultViewState(): void {
        this.currentPage.set(0);
        this.pageSize.set(DEFAULT_PAGE_SIZE);
        this.filters.set(emptyScheduleFilters());
        this.selectedFilterLabels.set({});
        this.filtersExpanded.set(false);
    }

    private persistViewState(): void {
        const key = this.storageKey();
        if (!key) return;
        const state: PersistedScheduleViewState = {
            filters: this.normalizeScheduleFilters(this.filters()),
            filterLabels: this.selectedFilterLabels(),
            filtersExpanded: this.filtersExpanded(),
            currentPage: this.currentPage(),
            pageSize: this.pageSize()
        };
        window.sessionStorage.setItem(key, JSON.stringify(state));
    }

    private restoreViewState(): void {
        const key = this.storageKey();
        if (!key) {
            this.applyDefaultViewState();
            return;
        }
        const raw = window.sessionStorage.getItem(key);
        if (!raw) {
            this.applyDefaultViewState();
            return;
        }
        try {
            const state = JSON.parse(raw) as Partial<PersistedScheduleViewState>;
            this.filters.set(this.normalizeScheduleFilters(state.filters));
            this.selectedFilterLabels.set(state.filterLabels || {});
            this.filtersExpanded.set(!!state.filtersExpanded);
            this.currentPage.set(Math.max(0, Number(state.currentPage || 0)));
            this.pageSize.set(this.normalizePageSize(state.pageSize));
        } catch {
            window.sessionStorage.removeItem(key);
            this.applyDefaultViewState();
        }
    }

    isFilterOptionsLoading(key: ScheduleFilterKey): boolean {
        return !!this.filterOptionsLoading()[key];
    }

    filterEmptyMessage(key: ScheduleFilterKey): string {
        return this.isFilterOptionsLoading(key) ? 'Loading options...' : 'No options found';
    }

    private scheduleStatusReason(row: PosmDeliverySchedule): string {
        const reason = this.cleanIssueText(row.statusReason);
        if (!reason) {
            return '';
        }
        const warningKeys = new Set(this.scheduleWarnings(row).map(warning => this.issueKey(warning)));
        const fragments = this.issueFragments(reason);
        const uniqueFragments = fragments.filter(fragment => !warningKeys.has(this.issueKey(fragment)));
        if (!uniqueFragments.length) {
            return '';
        }
        return uniqueFragments.join('. ');
    }

    private normalizePageSize(value: number | null | undefined): number {
        const requested = Number(value || DEFAULT_PAGE_SIZE);
        return PAGE_SIZE_OPTIONS.includes(requested) ? requested : DEFAULT_PAGE_SIZE;
    }

    private scheduleIssueDescriptor(message: string): Omit<ScheduleIssueGroup, 'items'> {
        const normalized = this.issueKey(message);
        if (normalized.includes('installation date')
            || normalized.includes('removal date')
            || normalized.includes('not scheduled')
            || normalized.includes('schedule')) {
            return {
                key: 'schedule',
                label: 'Scheduling',
                hint: 'Dates and planning readiness',
                icon: 'pi pi-calendar',
                tone: 'warn'
            };
        }
        if (normalized.includes('store location')
            || normalized.includes('location coordinates')
            || normalized.includes('location country')
            || normalized.includes('location city')
            || normalized.includes('location master')
            || normalized.includes('store master')
            || normalized.includes('store location country')
            || normalized.includes('store location city')) {
            return {
                key: 'store-data',
                label: 'Store master data',
                hint: 'Location mapping must be completed',
                icon: 'pi pi-map-marker',
                tone: 'danger'
            };
        }
        if (normalized.includes('allocate')
            || normalized.includes('allocation')
            || normalized.includes('reserved material')
            || normalized.includes('material allocation')
            || normalized.includes('stock-backed')
            || normalized.includes('coverage')) {
            return {
                key: 'materials',
                label: 'Materials',
                hint: 'Allocation or source coverage is incomplete',
                icon: 'pi pi-box',
                tone: 'danger'
            };
        }
        if (normalized.includes('order release')
            || normalized.includes('release')
            || normalized.includes('truck')
            || normalized.includes('shipment')
            || normalized.includes('delivery')) {
            return {
                key: 'execution',
                label: 'Execution flow',
                hint: 'Release or transport dependency is blocking progress',
                icon: 'pi pi-send',
                tone: 'warn'
            };
        }
        if (normalized.includes('return')
            || normalized.includes('removal')
            || normalized.includes('disposition')
            || normalized.includes('condition')) {
            return {
                key: 'return-flow',
                label: 'Return flow',
                hint: 'Removal or return outcome data is incomplete',
                icon: 'pi pi-replay',
                tone: 'warn'
            };
        }
        return {
            key: 'configuration',
            label: 'Configuration',
            hint: 'Record setup still needs attention',
            icon: 'pi pi-exclamation-triangle',
            tone: 'warn'
        };
    }

    private resolveFilterLabel(key: ScheduleFilterKey, value: any): FilterLabelValue {
        const values = this.filterValues(value);
        if (!values.length) {
            return this.isMultiFilterKey(key) ? [] : '';
        }
        const labels = values.map(item => this.resolveSingleFilterLabel(key, item)).filter(Boolean);
        return this.isMultiFilterKey(key) ? labels : (labels[0] || '');
    }

    private resolveSingleFilterLabel(key: ScheduleFilterKey, value: string): string {
        const option = this.filterOptions()[key].find(item => `${item.value}` === `${value}`);
        return option?.label || this.fallbackFilterLabel(key, value);
    }

    private fallbackFilterLabel(key: ScheduleFilterKey, value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        const raw = `${value}`.trim();
        if (!raw) {
            return '';
        }
        if (key === 'item') {
            const row = this.schedules().find(schedule =>
                `${schedule.inventoryItem?.id || ''}` === raw || `${schedule.projectItemId || ''}` === raw);
            return row ? this.itemLabel(row) : raw;
        }
        return raw;
    }

    private hydrateSelectedFilterOptions(): void {
        const filters = this.filters();
        const labels = this.selectedFilterLabels();
        this.filterOptions.update(current => {
            const next = { ...current };
            for (const key of Object.keys(filters) as ScheduleFilterKey[]) {
                const value = filters[key];
                const values = this.filterValues(value);
                if (!values.length) {
                    continue;
                }
                const existing = next[key] || [];
                const selectedOptions = values
                    .filter(item => !existing.some(option => `${option.value}` === item))
                    .map((item, index) => {
                        const label = this.labelAt(labels[key], index) || this.fallbackFilterLabel(key, item);
                        return label ? { label, value: item } : null;
                    })
                    .filter((option): option is PosmDeliveryScheduleFilterOption => !!option);
                if (selectedOptions.length) {
                    next[key] = [...selectedOptions, ...existing];
                }
            }
            return next;
        });
    }

    private mergeSelectedOption(key: ScheduleFilterKey, options: PosmDeliveryScheduleFilterOption[]): PosmDeliveryScheduleFilterOption[] {
        const value = this.filters()[key];
        const values = this.filterValues(value);
        if (!values.length) {
            return options;
        }
        const labels = this.selectedFilterLabels()[key];
        const selectedOptions = values
            .filter(item => !options.some(option => `${option.value}` === item))
            .map((item, index) => {
                const label = this.labelAt(labels, index) || this.fallbackFilterLabel(key, item);
                return label ? { label, value: item } : null;
            })
            .filter((option): option is PosmDeliveryScheduleFilterOption => !!option);
        return selectedOptions.length ? [...selectedOptions, ...options] : options;
    }

    private normalizeScheduleFilters(filters?: Partial<ScheduleFilters>): ScheduleFilters {
        const next = { ...emptyScheduleFilters(), ...(filters || {}) };
        next.retailer = this.filterValues(next.retailer);
        next.store = this.filterValues(next.store);
        next.city = this.filterValues(next.city);
        next.item = this.filterValues(next.item);
        return next;
    }

    private normalizeFilterValue(key: ScheduleFilterKey, value: any): string | string[] {
        if (this.isMultiFilterKey(key)) {
            return this.filterValues(value);
        }
        return `${value || ''}`.trim();
    }

    private filterValues(value: any): string[] {
        if (Array.isArray(value)) {
            return value.map(item => `${item || ''}`.trim()).filter(Boolean);
        }
        const text = `${value || ''}`.trim();
        return text ? text.split(',').map(item => item.trim()).filter(Boolean) : [];
    }

    private hasFilterValue(value: any): boolean {
        return this.filterValues(value).length > 0;
    }

    private isMultiFilterKey(key: ScheduleFilterKey): key is MultiScheduleFilterKey {
        return key === 'retailer' || key === 'store' || key === 'city' || key === 'item';
    }

    private summaryFilterLabel(key: ScheduleFilterKey, value: any, selectedLabels: FilterLabelValue | undefined): string {
        const values = this.filterValues(value);
        const labels = values.map((item, index) => this.labelAt(selectedLabels, index) || this.resolveSingleFilterLabel(key, item));
        return labels.filter(Boolean).join(', ');
    }

    private labelAt(labels: FilterLabelValue | undefined, index: number): string {
        if (Array.isArray(labels)) {
            return labels[index] || '';
        }
        return index === 0 ? (labels || '') : '';
    }

    private cleanIssueText(value: unknown): string {
        return `${value || ''}`.replace(/\s+/g, ' ').trim();
    }

    private issueKey(value: string): string {
        return this.cleanIssueText(value)
            .replace(/[.]+$/g, '')
            .toLowerCase();
    }

    private sameIssue(left: string, right: string): boolean {
        return this.issueKey(left) === this.issueKey(right);
    }

    private issueFragments(value: string): string[] {
        return this.cleanIssueText(value)
            .split(/[.;]\s+/)
            .map(fragment => fragment.trim())
            .filter(Boolean)
            .map(fragment => fragment.endsWith('.') ? fragment.slice(0, -1) : fragment);
    }

}
